import React, { useCallback, useState } from 'react'
import { IQuestionViewProps } from '../types'
import { SlidersQuestionModelTypes, SlidersResponse } from './types'
import useViewport from '../../modules/viewport/useViewport'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useSession from '@aytm/app/swidget/base/session/useSession'
import usePreviewOptions from '../../modules/preview/usePreviewOptions'
import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'

import Slider from '../../components/Slider'
import ImageAsset from '../../modules/images/ImageAsset'
import TextWithOptions from '../../modules/preview/components/TextWithOptions'
import { hapticEffects } from '../../lib/hapticEffects'

import style from './style.module.scss'
import commonStyle from '../../questions/_common/style.module.scss'

const SliderView = ({
  schema,
  response,
  onUpdateResponse,
  showValidation,
  responseValidation
}: IQuestionViewProps<SlidersQuestionModelTypes>) => {
  const viewport = useViewport()
  const session = useSession()
  const previewOptions = usePreviewOptions()
  const { t } = useSurveyLocale()
  const [touched, setTouched] = useState<boolean[]>([])
  const { localeOptions } = useInterfaceLocale()

  const hasSmallImages = schema.matrix_batches.some(batch => batch.image && !batch.image.expand && !batch.hidden)

  const disabled = false == session.rules?.show?.[schema.id] && !session.skipValidation
  const degree = schema.degree || schema.matrix_batches[0]?.answers?.length || 5

  const onChangeResponse = useCallback(
    (response: SlidersResponse) => {
      onUpdateResponse(response)
    },
    [onUpdateResponse]
  )

  const onSliderMove = (batchIndex: number, value: number) => {
    response.data[batchIndex].value = value
    onChangeResponse(response)
  }

  const onTouch = (batchIndex: number) => {
    touched[batchIndex] = true
    setTouched([...touched])
  }

  const findCurrentLabel = (index: number) => {
    const currentResponse = response.data[index]
    if (!currentResponse) return ''

    const batch = schema.matrix_batches.find(b => b.id === currentResponse.id)
    if (!batch) return ''

    const answer = batch.answers[currentResponse.value]
    return answer?.text ?? ''
  }

  return (
    <fieldset
      className={`${style.SliderView} ${localeOptions.rtl ? style.SliderViewRTL : ''} ${
        viewport.mobile ? commonStyle.mobileView + ' ' + style.mobile : style.desktop
      } ${disabled ? commonStyle.disabled : ''}`}
      data-testid="SW-MX"
      aria-describedby={`questionNote-${schema.id}`}
    >
      <legend className={commonStyle.srOnly}>{schema.text}</legend>
      {!schema.matrix_batches.filter(b => !b.hidden).length || !schema.matrix_batches[0]?.answers?.length ? (
        <div data-testid="SW-MX-NoAnswers">{t('questions', 'noAnswers')}</div>
      ) : (
        <>
          <div
            id={`questionNote-${schema.id}`}
            className={`${commonStyle.validationMessage}${
              showValidation && !responseValidation.valid ? ' ' + commonStyle.error : ''
            }`}
            data-testid="SW-MX-ValidationMessage"
          >
            {showValidation && !responseValidation.valid ? t('questions', 'mx', 'note') : t('questions', 'mx', 'note1')}
          </div>

          {schema.matrix_batches.map((batch, index) => (
            <div
              className={`${commonStyle.answerBlock} ${style.answerBlock} ${
                showValidation && responseValidation.invalidBatches?.includes(batch.id) ? style.error : ''
              } ${batch.hidden ? commonStyle.hidden : ''} ${
                null == response.data[index]?.value ? style.empty : style.filled
              } ${batch.fakeHidden ? commonStyle.fakeHidden : ''} ${
                null == response.data[index]?.value &&
                0 == response.data.filter((v, j) => j < index && null == v.value).length
                  ? style.focused
                  : ''
              }`}
              key={index}
              data-testid="SW-PS-Batch"
            >
              {batch.image && batch.image?.expand && (
                <div className={style.expandWrapper} data-testid="SW-Question-ExpandedImage">
                  <ImageAsset
                    asset={batch.image}
                    className={style.bigImage}
                    width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                    size="medium"
                    previewLabelTemplate={() => (
                      <TextWithOptions
                        text={batch.text}
                        options={previewOptions}
                        rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                      />
                    )}
                  />
                </div>
              )}
              <div className={style.plainBlock}>
                {batch.image && !batch.image?.expand && (
                  <ImageAsset
                    asset={batch.image}
                    className={style.Image}
                    width={66}
                    height={36}
                    size="small"
                    position={localeOptions.rtl ? 'right' : 'left'}
                    previewLabelTemplate={() => (
                      <TextWithOptions
                        text={batch.text}
                        options={previewOptions}
                        rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                      />
                    )}
                  />
                )}
                {(!batch.image || batch.image.expand) && hasSmallImages ? <div className={style.voidImage} /> : null}
                <div className={style.sliderBlock} data-testid="SW-PS-Slider">
                  <div className={style.title} data-testid="SW-MX-SubQuestion-Text">
                    <TextWithOptions
                      text={schema.matrix_batches.length > 0 ? batch.text : ''}
                      options={previewOptions}
                      rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                    />
                  </div>

                  {!touched[index] && (
                    <>
                      <div className={`${style.label} ${style.left}`} data-testid="SW-PS-LeftLabel">
                        <TextWithOptions
                          text={schema.matrix_batches[0].answers[0]?.text}
                          options={previewOptions}
                          rules={session.rules?.sub?.[schema.id]?.[schema.matrix_batches[0].answers[0]?.id]}
                        />
                      </div>
                      <div className={`${style.label} ${style.right}`} data-testid="SW-PS-RightLabel">
                        <TextWithOptions
                          text={schema.matrix_batches[0].answers[schema.matrix_batches[0].answers.length - 1]?.text}
                          options={previewOptions}
                          rules={
                            session.rules?.sub?.[schema.id]?.[
                              schema.matrix_batches[0].answers[schema.matrix_batches[0].answers.length - 1]?.id
                            ]
                          }
                        />
                      </div>
                    </>
                  )}
                  <>
                    <Slider
                      answerText={batch.text}
                      label={findCurrentLabel(index) || t('questions', 'mx', 'dragSlider')}
                      value={null == response.data[index]?.value ? null : response.data[index]?.value + 1}
                      onChange={value => onSliderMove(index, value - 1)}
                      onTouch={() => onTouch(index)}
                      className={`${style.slider} ${3 == degree ? style.nonRepeating : ''}`}
                      step={1}
                      min={1}
                      max={degree}
                      start="center"
                      ticks={true}
                      labelTemplate={value => (
                        <div className={commonStyle.sliderLabel}>
                          <TextWithOptions
                            text={value > 0 ? schema.matrix_batches[0].answers[value - 1]?.text : ''}
                            options={previewOptions}
                            rules={session.rules?.sub?.[schema.id]?.[schema.matrix_batches[0].answers[value - 1]?.id]}
                          />
                        </div>
                      )}
                      onSlideEnd={() => hapticEffects.selection()}
                    />
                  </>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </fieldset>
  )
}

export default SliderView
